/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { TranslatorDemosConfigs } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/TranslatorDemosConfigs';
import { VisualizedTextTranslationResults } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/VisualizedTextTranslationResults';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { textAnalyticsDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    textTranslationDocLink,
    textTranslationSampleLink,
    textTranslationSDKLink,
    translationPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { useTranslatorCapabilities } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { TextTranslationSwagger } from 'src/languageStudio/core/services/translation/translatorSwagger';

const messages = defineMessages({
    pageHeader: { id: 'languageStudio.textTranslationDemoPage.pageHeader', defaultMessage: 'Translate text' },
    capabilityDescription: {
        id: 'languageStudio.textTranslationDemoPage.capabilityDescription',
        defaultMessage: 'Translator adds multi-language user experiences in'
    },
    capabilityDescriptionCont: {
        id: 'languageStudio.textTranslationDemoPage.capabilityDescriptionCont',
        defaultMessage: `and can be used for text translation with any operating system.
        It is easy to integrate in your applications, websites, tools, and solutions.`
    },
    supportedLanguagesLink: {
        id: 'languageStudio.textTranslationDemoPage.supportedLanguagesLink',
        defaultMessage: '90 languages and dialects'
    },
    profanityMaskingOptionLabel: {
        id: 'languageStudio.textTranslationDemoPage.profanityMaskingOptionLabel',
        defaultMessage: 'Profanity masking'
    },
    transliterationOptionLabel: {
        id: 'languageStudio.textTranslationDemoPage.transliterationOptionLabel',
        defaultMessage: 'Show transliteration'
    },
    singleWordDictionaryOptionLabel: {
        id: 'languageStudio.textTranslationDemoPage.singleWordDictionaryOptionLabel',
        defaultMessage: 'Show dictionary for single word translation'
    },
    emptyState: {
        id: 'languageStudio.textTranslationDemoPage.emptyState',
        defaultMessage: `Your translated text results will appear here once you enter or
        upload some text and press Run. The file format needs to be .txt`
    }
});

/**
 * Displays the text translation demo page.
 */
export const TextTranslationDemoPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const azureResource = rootStore.azureStore.session.csAccountStore;
    const {
        tracker,
        resetResults,
        translateText,
        getCurlCommand,
        supportedLanguages,
        textTranslationResult,
        getSupportedLanguages
    } = useTranslatorCapabilities();
    useAsyncEffect(async () => {
        if (azureResource?.kind === 'TextTranslation') {
            getSupportedLanguages();
        }
    }, [azureResource?.kind]);

    const [document, setDocument] = React.useState('');
    const [transliteration, setTransliteration] = React.useState(true);
    const [profanityMasking, setProfanityMasking] = React.useState(false);
    const [sourceLanguage, setSourceLanguage] = React.useState<string>(null);
    const [targetLanguage, setTargetLanguage] = React.useState<string[]>([]);
    const [singleWordDictionary, setSingleWordDictionary] = React.useState(true);

    const description = (
        <LuisText>
            {localize(messages.capabilityDescription)}
            &nbsp;
            <ExternalLink>{localize(messages.supportedLanguagesLink)}</ExternalLink>
            &nbsp;
            {localize(messages.capabilityDescriptionCont)}
        </LuisText>
    );

    const onTargetLanguageChange = (newLanguageCodes: string[]) => {
        resetResults();
        setTargetLanguage(newLanguageCodes);
    };

    const onRenderConfigOptions = () => (
        <TranslatorDemosConfigs
            sourceLanguageKey={sourceLanguage}
            targetLanguageKeys={targetLanguage}
            supportedLanguages={supportedLanguages}
            onSourceLanguageChange={setSourceLanguage}
            onTargetLanguageChange={onTargetLanguageChange}
        />
    );

    const onRenderTextTranslatorOptions = (disabled: boolean) => (
        <Stack styles={{ root: { flex: 2 } }} tokens={{ childrenGap: 13 }}>
            <LuisText fontWeight={500}>{localize(commonDemosMessages.options)}</LuisText>

            <Toggle
                disabled={disabled}
                checked={profanityMasking}
                onText={localize(commonDemosMessages.onText)}
                offText={localize(commonDemosMessages.offText)}
                label={localize(messages.profanityMaskingOptionLabel)}
                onChange={(_, isChecked) => setProfanityMasking(isChecked)}
            />

            <Toggle
                disabled={disabled}
                checked={transliteration}
                onText={localize(commonDemosMessages.onText)}
                offText={localize(commonDemosMessages.offText)}
                label={localize(messages.transliterationOptionLabel)}
                onChange={(_, isChecked) => setTransliteration(isChecked)}
            />

            <Toggle
                disabled={disabled}
                checked={singleWordDictionary}
                onText={localize(commonDemosMessages.onText)}
                offText={localize(commonDemosMessages.offText)}
                label={localize(messages.singleWordDictionaryOptionLabel)}
                onChange={(_, isChecked) => setSingleWordDictionary(isChecked)}
            />
        </Stack>
    );

    const handleDocumentChange = (newDocument: string) => {
        resetResults();
        setDocument(newDocument);
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                demoVersion="3.0"
                featureName="Translate text"
                demoDescription={description}
                getSdkLink={textTranslationSDKLink}
                learnMoreLink={textTranslationDocLink}
                supportedPlatforms={['cloud', 'docker']}
                demoTitle={localize(messages.pageHeader)}
                documentationLink={textTranslationDocLink}
                gitHubSamplesLink={textTranslationSampleLink}
            />

            <DocumentInputStep
                document={document}
                documentLimit={10000}
                disableLanguageSelection
                featureName="Translate text"
                samplesList={textAnalyticsDemoSamples}
                displayAzureResourcePicker={false}
                acceptedResourceType="TextTranslation"
                onDocumentChange={handleDocumentChange}
                onRenderOptions={(def: () => React.ReactNode) => (
                    <Stack tokens={{ childrenGap: 25 }}>
                        {onRenderConfigOptions()}
                        {def?.()}
                    </Stack>
                )}
                containerStackProps={{ tokens: { maxWidth: '100%' } }}
                onRenderHorizontalOptions={onRenderTextTranslatorOptions}>
                <DemoPageAgreement
                    featureName="Translate text"
                    isRunDisabled={!document.length}
                    pricingLink={translationPricingLink}
                    acceptedResourceType="TextTranslation"
                    onRunDemoClick={() =>
                        translateText({
                            text: document,
                            transliteration,
                            targetLanguage: targetLanguage[0],
                            deleteProfanity: profanityMasking,
                            sourceLanguage: sourceLanguage?.includes('autoLanguageDetectionItemKey') ? null : sourceLanguage
                        })
                    }
                />
            </DocumentInputStep>

            <ResultsStep<TextTranslationSwagger>
                featureName="Translate text"
                isLoading={tracker.isRunning}
                results={textTranslationResult}
                emptyStateMessage={localize(messages.emptyState)}>
                <VisualizedTextTranslationResults
                    document={document}
                    results={textTranslationResult}
                    targetLanguage={targetLanguage[0]}
                    languagesList={supportedLanguages}
                />
            </ResultsStep>

            <NextStepsStep
                featureName="Translate text"
                sdkLink={textTranslationSDKLink}
                demosToDisplay={['ner', 'keyPhrases']}
                isResultsEmpty={!textTranslationResult}
                documentationLink={textTranslationDocLink}
                sampleCodeLink={textTranslationSampleLink}
                curlCommand={getCurlCommand({
                    text: document,
                    headersToAdd: `-H "Ocp-Apim-Subscription-Region: ${azureResource?.region ??
                        localize(commonDemosMessages.subscriptionRegionCurlPlaceholder)}"`,
                    endpoint: `translate?api-version=3.0&to=${targetLanguage?.[0]}&includeSentenceLength=true${
                        sourceLanguage !== 'autoLanguageDetectionItemKey' ? `&from=${sourceLanguage}` : ''
                    }${profanityMasking ? '&profanityAction=Deleted' : ''}${transliteration ? '&toScript=Latn' : ''}`
                })}
            />
        </Stack>
    );
});
